import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { localStorageSignal } from '../../helpers';

@Component({
  selector: 'app-pan-video',
  templateUrl: './pan-video.component.html',
  styleUrl: './pan-video.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PanVideoComponent {
  public readonly availablePlaybackSpeeds = [0.25, 0.5, 1, 2, 4] as const;

  public src = input.required<string>();
  public showControls = input<boolean>(true);
  private hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private videoElement = viewChild<ElementRef<HTMLVideoElement>>('videoElement');

  public expandedChange = output<boolean>();

  public zoomLevel = signal<number>(1);
  public expanded = signal<boolean>(false);
  public isPlaying = signal<boolean>(false);
  public nativeControlsVisible = signal<boolean>(true);
  public isDragging = signal<boolean>(false);
  public dragTipDismissed = signal<boolean>(false);
  public playbackSpeed = localStorageSignal<number>(1, 'video_playback_speed');
  private startX = 0;
  private startY = 0;
  private scrollLeft = 0;
  private scrollTop = 0;

  constructor() {
    effect(() => {
      this.nativeControlsVisible.set(this.showControls());
    });
  }

  public reload() {
    this.videoElement()?.nativeElement.load();
  }

  public async toggleExpanded() {
    if (document.fullscreenElement === this.hostElement.nativeElement) {
      await document.exitFullscreen();
    } else {
      await this.hostElement.nativeElement.requestFullscreen();
    }
  }

  @HostListener('document:fullscreenchange')
  public onFullscreenChange() {
    const expanded = document.fullscreenElement === this.hostElement.nativeElement;
    if (expanded === this.expanded()) return;

    this.expanded.set(expanded);
    this.expandedChange.emit(expanded);
  }

  public zoomIn() {
    this.zoomLevel.update((z) => Math.min(z + 0.5, 5));
  }

  public zoomOut() {
    this.zoomLevel.update((z) => Math.max(z - 0.5, 1));
  }

  public onMouseDown(event: MouseEvent, container: HTMLElement) {
    if (this.zoomLevel() <= 1) return;
    this.isDragging.set(true);
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.scrollLeft = container.scrollLeft;
    this.scrollTop = container.scrollTop;
  }

  public onMouseMove(event: MouseEvent, container: HTMLElement) {
    if (!this.isDragging() || this.zoomLevel() <= 1) return;
    event.preventDefault();
    container.scrollLeft = this.scrollLeft - (event.clientX - this.startX);
    container.scrollTop = this.scrollTop - (event.clientY - this.startY);
  }

  public setPlaybackSpeed(speed: number, videoElement: HTMLVideoElement) {
    this.playbackSpeed.set(speed);
    videoElement.playbackRate = speed;
  }

  public toggleNativeControls() {
    this.nativeControlsVisible.update((showControls) => !showControls);
  }
}
